import Config from '../Config';
import { JobStore } from '../../store';
import { PACKS } from './packHandler';
import { Validator } from './genericValidator';
import { isInternationalCity, capitalizeFirstLetter, difference } from '../../utils';
import { copyObject, isCityEmpty } from '../index';
import SessionStorage from '../../utils/SessionStorage';
import LocalStorageWrapper from '../../utils/LocalStorageWrapper';
import FreeLocality from '../JobsPostAd/others/freeLocality';
import CookieHelper from '../../utils/Apply/ApplyCookieHelper';
import { reduxActionAdAttributeChange } from '../../actions/PostAdActions/index';
import { POST_AD_FIELDS, updateCityField, updateLocalityFieldWithCity, PAP_CONSTANTS, defaultJobsPostAdRequestState } from './index';
import root from 'window-or-global';


const sessionStorageKey = 'jobsPostAdRequest';

export default class JobsPostAdRequest {
  /**
   * Set post ad request parameters once context is updated.
   * This happens on page load, once ajax response is available
   * from server.
   */
  static updatePostAdRequestFromContext = response => {
    let storeValues = JobStore.getState();
    let currentRequest = storeValues.postAdRequest;
    let user = storeValues.user;
    let cookieHelper = new CookieHelper();

    let newPayload = {};
    let jobTypeObject = currentRequest[POST_AD_FIELDS.TYPE];
    let minExpObject = currentRequest[POST_AD_FIELDS.MIN_EXPERIENCE];
    let maxExpObject = currentRequest[POST_AD_FIELDS.MAX_EXPERIENCE];

    jobTypeObject.options = response.json.jobTypes;
    newPayload[POST_AD_FIELDS.TYPE] = jobTypeObject;

    minExpObject.options = response.json.minExpOptions;
    minExpObject.defaultOptions = response.json.minExpOptions;
    newPayload[POST_AD_FIELDS.MIN_EXPERIENCE] = minExpObject;

    maxExpObject.options = response.json.maxExpOptions;
    maxExpObject.defaultOptions = response.json.maxExpOptions;
    newPayload[POST_AD_FIELDS.MAX_EXPERIENCE] = maxExpObject;

    // updatedRequest.hiringCompany.value = response.json.hireCompanyName;
    // updatedRequest.postedByCompany.value = response.json.postedByCompany;
    JobsPostAdRequest.reduxUpdateCompanyDetails();

    if (user.loggedIn) {
      let recruiterEmailObject = currentRequest[POST_AD_FIELDS.RECRUITER_EMAIL];
      let recruiterMobileObject = currentRequest[POST_AD_FIELDS.RECRUITER_PHONE];

      if (user.hasOwnProperty('email') && Validator.isEmpty(recruiterEmailObject.value)) {
        recruiterEmailObject.value = user.email;
        recruiterEmailObject.options = user.emailList;
        newPayload[POST_AD_FIELDS.RECRUITER_EMAIL] = recruiterEmailObject;
      }

      if (user.hasOwnProperty('mobile') && Validator.isEmpty(recruiterMobileObject.value)) {
        recruiterMobileObject.value = JSON.stringify(user.mobile);
        recruiterMobileObject.options = user.mobileList;
        newPayload[POST_AD_FIELDS.RECRUITER_PHONE] = recruiterMobileObject;
      }
    }

    // let cookieCity = cookieHelper.getPreferredCity();
    let preferredCity = capitalizeFirstLetter(cookieHelper.getPreferredCity());
    let preferredCityId = cookieHelper.getPreferredCityId();

    let cityObject = currentRequest[POST_AD_FIELDS.CITY];
    let localityObject = currentRequest[POST_AD_FIELDS.LOCALITY];
    let roleName = currentRequest && currentRequest.role && currentRequest.role.valueObject ? currentRequest.role.valueObject.value : "";
    if (cityObject.id && -1 !== cityObject.id && cityObject.value) {
      preferredCity = cityObject.value;
      preferredCityId = cityObject.id;
    }

    if (('undefined' !== typeof preferredCityId) && !isCityEmpty(preferredCity) && !isInternationalCity(preferredCity)) {
      if (cityObject.id !== preferredCityId) {
        cityObject = updateCityField(cityObject, preferredCity, preferredCityId);
        newPayload[POST_AD_FIELDS.CITY] = cityObject;

        localityObject = updateLocalityFieldWithCity(localityObject, preferredCity);
        newPayload[POST_AD_FIELDS.LOCALITY] = localityObject;
      }
      Config.updateConfigForCategory({
        cityId: preferredCityId,
        subCatId: currentRequest[POST_AD_FIELDS.TYPE].selectedId,
        cityname: preferredCity,
        roleName: roleName
      });
    } else {
      Config.updateConfigForCategory({
        subCatId: currentRequest[POST_AD_FIELDS.TYPE].selectedId
      });
    }

    // update pack container version in request
    newPayload['packContainerVersion'] = JobsPostAdRequest.updatePackContainerVersion(currentRequest.packContainerVersion);

    JobsPostAdRequest.updatePostAdRequest(newPayload);
  };

  static updateRedux = _ => {
    JobsPostAdRequest.reduxUpdatePackContainerVersion();
    JobsPostAdRequest.reduxUpdateCompanyDetails();
    JobsPostAdRequest.reduxUpdateSelectedPacks();
    FreeLocality.setFullCityDetails();
    FreeLocality.setLocalityMaxSelectionLimit();
  };

  /** Used to reset selected packs and sub packs,
   * when avaialble pack option changes
  */
  static reduxUpdateSelectedPacks = _ => {
    let { user, postAdRequest } = JobStore.getState();
    let existingOthersObject = postAdRequest.others;
    let othersObject = copyObject(postAdRequest.others);
    if (user.hasCredits) {
      othersObject.selectedSubPack = null;
      othersObject.selectedPack = PACKS.GOLD;
    }

    let diffObject = difference(othersObject, existingOthersObject);

    if(0 !== Object.keys(diffObject).length) {
      JobsPostAdRequest.updatePostAdRequest({
        others: othersObject
      });
    }
  };

  static reduxUpdateCompanyDetails = _ => {
    let { user, postAdRequest } = JobStore.getState();
    let existingHiringCompanyObject = postAdRequest[POST_AD_FIELDS.HIRING_COMPANY];
    let existingPostedCompanyObject = postAdRequest[POST_AD_FIELDS.POSTED_BY_COMPANY];
    let hiringCompanyObject = copyObject(existingHiringCompanyObject);
    let postedCompanyObject = copyObject(existingPostedCompanyObject);

    if (user.hasCredits) {
      if ('' === hiringCompanyObject.value)
        hiringCompanyObject.value = user.companyDetails.kycCompany;

      if (true === user.companyDetails.isToDisplayIndividual) {
        postedCompanyObject.value = 'Individual';
        postedCompanyObject.show = true;
      } else if (user.companyDetails.companyName) {
        postedCompanyObject.value = user.companyDetails.companyName;
        postedCompanyObject.show = true;
      }
    } else if ( postAdRequest.locality.value.length <= PAP_CONSTANTS.MAX_FREE_LOCALITY ) {
      postedCompanyObject.value = 'Individual';
      postedCompanyObject.show = true;
    }

    let hiringCompanyDifference = difference(hiringCompanyObject, existingHiringCompanyObject);
    let postedCompanyDifference = difference(postedCompanyObject, existingPostedCompanyObject);

    let newPayload = {};
    let isDispatchRequired = false;
    if (0 !== Object.keys(hiringCompanyDifference).length) {
      newPayload[POST_AD_FIELDS.HIRING_COMPANY] = hiringCompanyObject;
      isDispatchRequired = true;
    }

    if (0 !== Object.keys(postedCompanyDifference).length) {
      newPayload[POST_AD_FIELDS.POSTED_BY_COMPANY] = postedCompanyObject;
      isDispatchRequired = true;
    }

    if (isDispatchRequired) {
      JobsPostAdRequest.updatePostAdRequest(newPayload);
    }
  };

  static updatePackContainerVersion = currentVersion => {
    return ++currentVersion;
  };

  static reduxUpdatePackContainerVersion = _ => {
    let { postAdRequest } = JobStore.getState();

    JobsPostAdRequest.updatePostAdRequest({
      packContainerVersion: ++postAdRequest.packContainerVersion
    });
  };

  static updatePostAdRequest = (updatedRequest, message) => {
    if(!Validator.isEmpty(message))
      console.log(message);

    JobStore.dispatch(reduxActionAdAttributeChange(updatedRequest));
  };

  static getDefaultState = () => {
    // let defaultStateFromStorage = JobsPostAdRequest.getPostAdRequestFromStorage();
    let defaultState;
    // if (('undefined' === typeof defaultStateFromStorage) || (null === defaultStateFromStorage)) {
      defaultState = defaultJobsPostAdRequestState;
    // } else {
      // defaultState = JSON.parse(defaultStateFromStorage);
    // }

    return defaultState;
  };

  static isDefaultStateSaved = () => {
    let defaultStateFromStorage = JobsPostAdRequest.getPostAdRequestFromStorage();
    return !!defaultStateFromStorage;
  };

  static removePostAdRequestFromStorage = () => {
    SessionStorage.removeItem(sessionStorageKey);
    LocalStorageWrapper.removeItem(sessionStorageKey);
  };

  static getPostAdRequest = () => copyObject(JobStore.getState().postAdRequest);

  static savePostAdRequest = (request, isPermanent) => {
    // set currentStep to 0 on saving state
    request.currentStep = 0;
    // set snackbar message to empty while saving state
    request.others.message = '';

    let updatedRequest = copyObject(request);
    updatedRequest.showLoader = false;
    updatedRequest.showOTP = false;
    updatedRequest.showUnlimitedFreeAdsBanner = false;
    if (isPermanent) {
      LocalStorageWrapper.setItem(sessionStorageKey, JSON.stringify(updatedRequest));
    } else {
      SessionStorage.setItem(sessionStorageKey, JSON.stringify(updatedRequest));
    }
  };

  static _saveAd = (isPermanent) => JobsPostAdRequest.savePostAdRequest(JobStore.getState().postAdRequest, isPermanent);

  static showSnackbar = (message) => {
    if (!Validator.isEmpty(message)) {
      let { postAdRequest } = copyObject(JobStore.getState());
      let othersObject = postAdRequest.others;
      othersObject.message = message;
      let updatedRequest = {
        others: othersObject
      };
      JobsPostAdRequest.updatePostAdRequest(updatedRequest);
    }
  };

  static showLoader = () => {
    JobsPostAdRequest.updatePostAdRequest({
      showLoader: true
    });
  };

  static hideLoader = () => {
    JobsPostAdRequest.updatePostAdRequest({
      showLoader: false
    });
  };

  static showUnlimitedFreeAdsBanner = (freePostAdActiveBannerMsg) => {
    JobsPostAdRequest.updatePostAdRequest({
      showUnlimitedFreeAdsBanner: true,
      freePostAdActiveBannerMsg
    });
  };

  static getPostAdRequestFromStorage = () => {
    const data = (SessionStorage.getItem(sessionStorageKey)) || (LocalStorageWrapper.getItem(sessionStorageKey));
    return data ? JSON.parse(data) : null;
  }

}

root.saveAd = JobsPostAdRequest._saveAd;



// WEBPACK FOOTER //
// ../q-jobs/src/utils/JobsPostAd/jobsPostAdRequest.js